import { create } from 'zustand';
import {
  FeatureFlagId,
  FeatureFlagsState,
  DEFAULT_FEATURE_FLAGS,
  FEATURE_FLAG_PATHS,
  FEATURE_FLAG_PATH_ALIASES,
  FEATURE_FLAG_IDS,
  FEATURE_FLAG_FALLBACK_ORDER,
  DEFAULT_NAV_PLACEMENT,
  DEFAULT_PARTICIPANT_LABEL_SINGULAR,
  DEFAULT_PARTICIPANT_LABEL_PLURAL,
  DEFAULT_COURSE_LABEL_SINGULAR,
  DEFAULT_COURSE_LABEL_PLURAL,
  DEFAULT_CLASSIFICATION_BANNER_MODE,
  type NavPlacement, type ClassificationBannerMode,
} from '../types/featureFlags';
import { getModuleSettings, updateModuleSettings, type ModuleSettingsPayload } from '../services/moduleSettings';

interface FeatureFlagsStore extends FeatureFlagsState {
  /** Sidebar (left) or horizontal top navigation bar */
  navPlacement: NavPlacement;
  setNavPlacement: (placement: NavPlacement) => void;
  participantLabelSingular: string;
  participantLabelPlural: string;
  setParticipantLabelSingular: (value: string) => void;
  setParticipantLabelPlural: (value: string) => void;
  courseLabelSingular: string;
  courseLabelPlural: string;
  setCourseLabelSingular: (value: string) => void;
  setCourseLabelPlural: (value: string) => void;
  classificationBannerMode: ClassificationBannerMode;
  setClassificationBannerMode: (mode: ClassificationBannerMode) => void;
  setFlag: (id: FeatureFlagId, enabled: boolean) => void;
  setFlags: (flags: Partial<FeatureFlagsState>) => void;
  resetToDefaults: () => void;
  loadFromServer: () => Promise<void>;
  isEnabled: (id: FeatureFlagId) => boolean;
  /** True if the route is enabled (use for nav and route gating) */
  isPathEnabled: (path: string) => boolean;
  /** First enabled tab path for redirects when a feature is off (always ends at /settings if none). */
  getFirstEnabledFallbackPath: (opts?: { student?: boolean }) => string;
}

let saveTimeout: ReturnType<typeof setTimeout> | null = null;
let suppressSave = false;
const MODULE_SETTINGS_CACHE_KEY = 'module-settings-cache';

const isClassificationBannerMode = (value: unknown): value is ClassificationBannerMode =>
  value === 'commercial' || value === 'unclassified' || value === 'cui';

const readCachedModuleSettings = (): Partial<ModuleSettingsPayload> => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(MODULE_SETTINGS_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Partial<ModuleSettingsPayload>;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const toModuleSettingsPayload = (state: FeatureFlagsStore): ModuleSettingsPayload => {
  const payload = FEATURE_FLAG_IDS.reduce((acc, id) => {
    acc[id] = state[id];
    return acc;
  }, {} as Record<FeatureFlagId, boolean>);

  return {
    ...payload,
    navPlacement: state.navPlacement,
    participantLabelSingular: state.participantLabelSingular,
    participantLabelPlural: state.participantLabelPlural,
    courseLabelSingular: state.courseLabelSingular,
    courseLabelPlural: state.courseLabelPlural,
    classificationBannerMode: state.classificationBannerMode,
  };
};

const writeCachedModuleSettings = (state: FeatureFlagsStore | ModuleSettingsPayload) => {
  if (typeof window === 'undefined') return;
  try {
    const payload = 'setFlag' in state ? toModuleSettingsPayload(state) : state;
    window.localStorage.setItem(MODULE_SETTINGS_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // Cache is best-effort; server settings remain the source of truth.
  }
};

const cachedModuleSettings = readCachedModuleSettings();
const cachedFeatureFlags = FEATURE_FLAG_IDS.reduce((acc, id) => {
  if (typeof cachedModuleSettings[id] === 'boolean') {
    acc[id] = cachedModuleSettings[id];
  }
  return acc;
}, {} as Partial<FeatureFlagsState>);

const scheduleSaveToServer = (state: FeatureFlagsStore) => {
  if (suppressSave) {
    return;
  }
  writeCachedModuleSettings(state);
  if (saveTimeout) {
    clearTimeout(saveTimeout);
  }
  saveTimeout = setTimeout(async () => {
    try {
      await updateModuleSettings(toModuleSettingsPayload(state));
    } catch (error) {
      console.error('[FeatureFlags] Failed to save module settings:', error);
    }
  }, 500);
};

/** For subscriptions: changes when any tab/module flag toggles (nav + route gates). */
export const selectFeatureFlagsFingerprint = (s: FeatureFlagsStore) =>
  FEATURE_FLAG_IDS.map((id) => (s[id] ? '1' : '0')).join('');

export const useFeatureFlagsStore = create<FeatureFlagsStore>()((set, get) => ({
  ...DEFAULT_FEATURE_FLAGS,
  ...cachedFeatureFlags,
  navPlacement:
    cachedModuleSettings.navPlacement === 'top' || cachedModuleSettings.navPlacement === 'left'
      ? cachedModuleSettings.navPlacement
      : DEFAULT_NAV_PLACEMENT,
  participantLabelSingular:
    cachedModuleSettings.participantLabelSingular || DEFAULT_PARTICIPANT_LABEL_SINGULAR,
  participantLabelPlural:
    cachedModuleSettings.participantLabelPlural || DEFAULT_PARTICIPANT_LABEL_PLURAL,
  courseLabelSingular:
    cachedModuleSettings.courseLabelSingular || DEFAULT_COURSE_LABEL_SINGULAR,
  courseLabelPlural:
    cachedModuleSettings.courseLabelPlural || DEFAULT_COURSE_LABEL_PLURAL,
  classificationBannerMode: isClassificationBannerMode(cachedModuleSettings.classificationBannerMode)
    ? cachedModuleSettings.classificationBannerMode
    : DEFAULT_CLASSIFICATION_BANNER_MODE,

  setNavPlacement: (placement) =>
    set((state) => {
      const next = { ...state, navPlacement: placement };
      scheduleSaveToServer(next);
      return next;
    }),

  setParticipantLabelSingular: (value) =>
    set((state) => {
      const next = { ...state, participantLabelSingular: value };
      scheduleSaveToServer(next);
      return next;
    }),

  setParticipantLabelPlural: (value) =>
    set((state) => {
      const next = { ...state, participantLabelPlural: value };
      scheduleSaveToServer(next);
      return next;
    }),

  setCourseLabelSingular: (value) =>
    set((state) => {
      const next = { ...state, courseLabelSingular: value };
      scheduleSaveToServer(next);
      return next;
    }),

  setCourseLabelPlural: (value) =>
    set((state) => {
      const next = { ...state, courseLabelPlural: value };
      scheduleSaveToServer(next);
      return next;
    }),

  setClassificationBannerMode: (mode) =>
    set((state) => {
      const next = { ...state, classificationBannerMode: mode };
      scheduleSaveToServer(next);
      return next;
    }),

  setFlag: (id, enabled) =>
    set((state) => {
      const next = { ...state, [id]: enabled };
      scheduleSaveToServer(next as FeatureFlagsStore);
      return next;
    }),

  setFlags: (flags) =>
    set((state) => {
      const next = { ...state, ...flags };
      scheduleSaveToServer(next as FeatureFlagsStore);
      return next;
    }),

  resetToDefaults: () =>
    set((state) => {
      const next = {
        ...state,
        ...DEFAULT_FEATURE_FLAGS,
        navPlacement: DEFAULT_NAV_PLACEMENT,
        participantLabelSingular: DEFAULT_PARTICIPANT_LABEL_SINGULAR,
        participantLabelPlural: DEFAULT_PARTICIPANT_LABEL_PLURAL,
        courseLabelSingular: DEFAULT_COURSE_LABEL_SINGULAR,
        courseLabelPlural: DEFAULT_COURSE_LABEL_PLURAL,
        classificationBannerMode: DEFAULT_CLASSIFICATION_BANNER_MODE,
      };
      scheduleSaveToServer(next);
      return next;
    }),

  loadFromServer: async () => {
    try {
      const server = await getModuleSettings();
      writeCachedModuleSettings(server);
      suppressSave = true;
      set((state) => ({
        ...state,
        dashboard: server.dashboard,
        analysis: server.analysis,
        scheduledAnalyses: server.scheduledAnalyses,
        results: server.results,
        cop: server.cop,
        pathfinder: server.pathfinder,
        linkAnalysis: server.linkAnalysis,
        patternOfLife: server.patternOfLife,
        itinerary: server.itinerary,
        dashboardBuilder: server.dashboardBuilder,
        rfDashboard: server.rfDashboard,
        adtech: server.adtech,
        overwatch: server.overwatch,
        intel: server.intel,
        navPlacement: server.navPlacement,
        participantLabelSingular: server.participantLabelSingular,
        participantLabelPlural: server.participantLabelPlural,
        courseLabelSingular: server.courseLabelSingular,
        courseLabelPlural: server.courseLabelPlural,
        classificationBannerMode: server.classificationBannerMode,
      }));
    } catch (error) {
      console.error('[FeatureFlags] Failed to load module settings:', error);
    } finally {
      suppressSave = false;
    }
  },

  isEnabled: (id) => get()[id] === true,

  isPathEnabled: (path) => {
    const normalized = path.replace(/\/$/, '') || '/';
    for (const [id, p] of Object.entries(FEATURE_FLAG_PATHS)) {
      const candidates = [p, ...(FEATURE_FLAG_PATH_ALIASES[id as FeatureFlagId] || [])];
      if (candidates.some((c) => c === normalized || path.startsWith(c + '/'))) {
        return get()[id as FeatureFlagId] === true;
      }
    }
    return true;
  },

  getFirstEnabledFallbackPath: (opts) => {
    const state = get();
    const base = [...FEATURE_FLAG_FALLBACK_ORDER];
    const order: FeatureFlagId[] = opts?.student
      ? ['itinerary', ...base.filter((id) => id !== 'itinerary')]
      : base;
    for (const id of order) {
      const p = FEATURE_FLAG_PATHS[id];
      if (state[id] && p) return p;
    }
    return '/settings';
  },
}));
